import React from 'react'
import { captureException } from '@sentry/react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../common/Toast/toast-type'
import { Modal } from '../../common/Modal'
import { ModalHeader } from '../../common/Modal/ModalHeader'
import { theme } from '../../../theme/theme'
import { ModalBody } from '../../common/Modal/ModalBody'
import { Button, ButtonVariant } from '../../common/Button'
import { IconChoices } from '../../common/Icon'
import {
  downloadApartmentRegister,
  downloadMemberRegister,
  uploadApartmentRegister,
  uploadMemberRegister,
} from '../../../api/societies'
import { openUrl } from '../../../helpers/url'
import { RegisterExcelImportForm } from './RegisterExcelImportForm'
import { societyRegisterUploadSchema } from '../../../forms/schemas/society_register_upload'
import { i18nSocietyType } from '../../../i18n'

export enum ExcelImportModalVariant {
  ENTRANCE_UNIT = 0,
  MEMBER = 1,
}

interface RegisterExcelImportModalProps {
  show: boolean
  close: () => void
  variant: ExcelImportModalVariant
  children?: React.ReactNode
}

export const RegisterExcelImportModal = ({
  show,
  close,
  variant,
  children,
}: RegisterExcelImportModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('registerExcelImport.flashMessage.uploadRegisterSuccess')
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('registerExcelImport.flashMessage.uploadRegisterFailure')
    )
  }

  const {
    mutate: mutateUploadMemberRegister,
    isPending: isUploadingMemberRegister,
  } = useMutation({
    mutationFn: uploadMemberRegister,
    onSuccess: () => {
      showSuccessMessage()
      close()
    },
    onError: () => {
      showErrorMessage()
    },
  })
  const {
    mutate: mutateUploadApartmentRegister,
    isPending: isUploadingApartmentRegister,
  } = useMutation({
    mutationFn: uploadApartmentRegister,
    onSuccess: () => {
      showSuccessMessage()
      close()
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const onSubmit = async (
    data: InferType<typeof societyRegisterUploadSchema>
  ): Promise<void> => {
    const formData = new FormData()
    const blob = await fetch(data.file.base64).then((res) => res.blob())
    formData.append('file', blob, data.file.name)

    if (variant === ExcelImportModalVariant.MEMBER) {
      society &&
        mutateUploadMemberRegister({ id: society?._id, body: formData })
    } else {
      society &&
        mutateUploadApartmentRegister({ id: society?._id, body: formData })
    }
  }

  const onError = (): void => {
    setToastNotification(ToastType.DANGER, 'TODO')
  }

  const { mutate: mutateDownloadRegister, isPending: isDownLoadingRegister } =
    useMutation({
      mutationFn: async (variables: {
        variant: ExcelImportModalVariant
        societyId: string
      }) => {
        const response =
          variant === ExcelImportModalVariant.MEMBER
            ? await downloadMemberRegister(variables.societyId, true)
            : await downloadApartmentRegister(variables.societyId)
        return response
      },
      onSuccess: (response) => {
        try {
          const { endpoint } = response.data.data
          openUrl(endpoint)
        } catch (error) {
          setToastNotification(
            ToastType.DANGER,
            translate('flashMessage.somethingWentWrongError')
          )
          captureException(error)
        }
        showSuccessMessage()
      },
      onError: (error) => {
        captureException(error)
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongError')
        )
      },
    })

  const _downloadRegister = async (): Promise<void> => {
    if (!society) return
    mutateDownloadRegister({ variant, societyId: society._id })
  }

  const loading = isUploadingMemberRegister || isUploadingApartmentRegister
  const buttonsDisabled = isDownLoadingRegister || loading

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              <div>
                <p style={theme.textVariants.h3}>
                  {variant === ExcelImportModalVariant.ENTRANCE_UNIT
                    ? translate('registerExcelImport.modal.entranceUnitTitle', {
                        context: i18nSocietyType(
                          society?.societyTypeDisplayName
                        ),
                      })
                    : translate('registerExcelImport.modal.memberTitle', {
                        context: i18nSocietyType(
                          society?.societyTypeDisplayName
                        ),
                      })}
                </p>
              </div>
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <p style={theme.textVariants.baseBold}>
                {variant === ExcelImportModalVariant.ENTRANCE_UNIT
                  ? translate(
                      'registerExcelImport.modal.entranceUnitSubtitle',
                      {
                        context: i18nSocietyType(
                          society?.societyTypeDisplayName
                        ),
                      }
                    )
                  : translate('registerExcelImport.modal.memberSubtitle', {
                      context: i18nSocietyType(society?.societyTypeDisplayName),
                    })}
              </p>
              <div style={theme.textVariants.base} className="mt-1 mb-3">
                {(variant === ExcelImportModalVariant.ENTRANCE_UNIT
                  ? translate(
                      'registerExcelImport.modal.entranceUnitDescription',
                      {
                        context: i18nSocietyType(
                          society?.societyTypeDisplayName
                        ),
                      }
                    )
                  : translate('registerExcelImport.modal.memberDescription', {
                      context: i18nSocietyType(society?.societyTypeDisplayName),
                    })
                )
                  .split('\n\n')
                  .map((_str) => (
                    <p key={_str} style={theme.textVariants.base}>
                      {_str}
                    </p>
                  ))}
              </div>
              <Button
                variant={ButtonVariant.PRIMARY}
                label="Ladda ner register"
                icon={IconChoices.DOWNLOAD}
                onClick={_downloadRegister}
                loading={isDownLoadingRegister}
                disabled={buttonsDisabled}
              />
              <RegisterExcelImportForm
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                className="mt-2"
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
